import { API_CONFIG } from '../config/config';
import apiClient from '../core/apiClient';

// Event for credit updates
export const CREDIT_UPDATE_EVENT = 'CREDIT_UPDATE';

// Create event emitter
const eventTarget = new EventTarget();

// Add event listener
export const onCreditUpdate = (callback) => {
  eventTarget.addEventListener(CREDIT_UPDATE_EVENT, callback);
  return () => {
    eventTarget.removeEventListener(CREDIT_UPDATE_EVENT, callback);
  };
};

// Dispatch event
const dispatchCreditUpdate = () => {
  eventTarget.dispatchEvent(new Event(CREDIT_UPDATE_EVENT));
};

export const billingService = {
  async getStatus() {
    return apiClient.get(API_CONFIG.ENDPOINTS.BILLING.STATUS);
  },
  
  async checkout(credits) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.BILLING.CHECKOUT, { credits });
    dispatchCreditUpdate();
    return response;
  },

  async startCreditPackCheckout(packId) {
    const url = `${API_CONFIG.ENDPOINTS.BILLING.CREDIT_PACK}?pack=${packId}`;
    const res = await apiClient.post(url);
    return res.data;
  },
  
  async createCheckoutSession(credits) {
    console.log('Creating checkout session with credits:', credits);
    const res = await apiClient.post(API_CONFIG.ENDPOINTS.BILLING.CHECKOUT, { credits });
    return res.data;
  },
  
  async getCreditHistory(params = {}) {
    const {
      limit = 50,
      startAfter = null,
      transactionType = null,
      startDate = null,
      endDate = null
    } = params;

    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit);
    if (startAfter) queryParams.append('start_after', startAfter);
    if (transactionType) queryParams.append('transaction_type', transactionType);
    if (startDate) queryParams.append('start_date', startDate);
    if (endDate) queryParams.append('end_date', endDate);

    const url = `${API_CONFIG.ENDPOINTS.CREDITS.HISTORY}?${queryParams.toString()}`;
    const response = await apiClient.get(url);
    const data = response.data || {};
    return {
      ...data,
      hasMore: typeof data.hasMore === 'boolean' ? data.hasMore : Boolean(data.has_more),
      lastDocId: data.lastDocId ?? data.last_doc_id ?? null,
    };
  },

  async getCreditStats(days = 30) {
    const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.CREDITS.STATS}?days=${days}`);
    return response.data;
  },

  // Admin functions
  async adjustCredits(userId, amount, reason) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.CREDITS.ADMIN_ADJUST, {
      user_id: userId,
      amount,
      reason
    });
    dispatchCreditUpdate();
    return response.data;
  },

  async addBonusCredits(userId, amount, reason) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.CREDITS.BONUS_ADD, {
      user_id: userId,
      amount,
      reason
    });
    dispatchCreditUpdate();
    return response.data;
  },

  async processRefund(userId, amount, transactionId, reason) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.CREDITS.REFUND, {
      user_id: userId,
      amount,
      transaction_id: transactionId,
      reason
    });
    dispatchCreditUpdate();
    return response.data;
  },

  // Utility function to manually trigger credit update events
  notifyCreditUpdate() {
    dispatchCreditUpdate();
  },

  async startSubscriptionCheckout(payload = {}) {
    const res = await apiClient.post(API_CONFIG.ENDPOINTS.BILLING.SUBSCRIPTION_CHECKOUT, payload);
    return res.data;
  },

  async cancelSubscription() {
    const res = await apiClient.post(API_CONFIG.ENDPOINTS.BILLING.SUBSCRIPTION_CANCEL);
    dispatchCreditUpdate();
    return res.data;
  },

  async reactivateSubscription() {
    const res = await apiClient.post(API_CONFIG.ENDPOINTS.BILLING.SUBSCRIPTION_REACTIVATE);
    dispatchCreditUpdate();
    return res.data;
  },

  async syncSubscription() {
    const res = await apiClient.get(API_CONFIG.ENDPOINTS.BILLING.SUBSCRIPTION_SYNC);
    return res.data;
  },

  async syncCheckoutSession(sessionId) {
    const res = await apiClient.post(API_CONFIG.ENDPOINTS.BILLING.CHECKOUT_SYNC, { session_id: sessionId });
    dispatchCreditUpdate();
    return res.data;
  }
};
